import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { SpamMarkResponse, SpamMarkedSessionDto } from '@/types';

/**
 * OP-1005 - the three "Spam / unwanted" calls, all against SessionManagementService through the
 * gateway (the same base path as GET /api/sessions and POST /api/sessions/{id}/close). The server
 * gates every one of them on `workspace.handle`; the console's own permission check only decides
 * whether to OFFER the action.
 */

/**
 * Marks an email conversation as spam / unwanted. `blockSender` is sent as a strict boolean, so
 * "unchecked" is an explicit `false` on the wire rather than an absent field the server has to
 * default. Rejects (never resolves with a fallback) on any non-2xx, so the caller can keep the
 * conversation on screen when the mark did not happen.
 */
export async function markSessionSpam(sessionId: string, blockSender: boolean): Promise<SpamMarkResponse> {
  const res = await axiosClient.post<SpamMarkResponse>(API.SESSIONS.SPAM_MARK(sessionId), {
    blockSender: blockSender === true,
  });
  return res.data;
}

/** Undoes a spam mark inside the grace window. 409 (not marked) and 404 reject like any failure. */
export async function undoSessionSpam(sessionId: string): Promise<void> {
  await axiosClient.post(API.SESSIONS.SPAM_UNDO(sessionId));
}

/** The tenant's conversations currently marked as spam, newest first. */
export async function listSpamSessions(): Promise<SpamMarkedSessionDto[]> {
  const res = await axiosClient.get<SpamMarkedSessionDto[]>(API.SESSIONS.SPAM_LIST);
  // A bare array is the contract; anything else (a proxy's HTML page, a paged envelope) is an empty
  // list rather than a crash in the view.
  return Array.isArray(res.data) ? res.data : [];
}

const MAX_SERVER_ERROR_CHARS = 300;

/**
 * The server's own `{ error }` text from a failed call, or null when it did not send one.
 *
 * Only the documented `{ error: string }` body is surfaced: a string body (an HTML error page from
 * a proxy) or a missing body is NOT shown, and the caller falls back to its own message. The text is
 * rendered as a plain text node (React-escaped, never as HTML) and capped in length.
 */
export function serverErrorText(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const text = (data as { error?: unknown }).error;
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  return trimmed.length > MAX_SERVER_ERROR_CHARS
    ? trimmed.slice(0, MAX_SERVER_ERROR_CHARS).trimEnd() + '…'
    : trimmed;
}
